// Per-user left-sidebar colour scheme.
//
// The server owns the value (so it follows the user across devices) but the
// last choice is mirrored into localStorage, which is what the initial state
// reads — that way the sidebar paints in the correct colours on first render
// instead of flashing the default while the fetch is in flight.
//
// The root reducer (store/index.ts) resets this slice on logout, so the next
// user on the same browser does not inherit the previous user's theme.

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  SidebarThemeId,
  DEFAULT_SIDEBAR_THEME,
  cacheSidebarTheme,
  clearCachedSidebarTheme,
  readCachedSidebarTheme,
} from '../config/sidebarThemes';
import { sidebarThemeService } from '../services/sidebarThemeService';

interface SidebarThemeState {
  theme: SidebarThemeId;
  status: 'idle' | 'loading' | 'loaded' | 'error';
  saving: boolean;
  error: string | null;
}

const initialState: SidebarThemeState = {
  theme: readCachedSidebarTheme(),
  status: 'idle',
  saving: false,
  error: null,
};

export const fetchSidebarTheme = createAsyncThunk('sidebarTheme/fetch', async () => {
  return await sidebarThemeService.get();
});

export const saveSidebarTheme = createAsyncThunk(
  'sidebarTheme/save',
  async (theme: SidebarThemeId) => {
    return await sidebarThemeService.save(theme);
  }
);

const sidebarThemeSlice = createSlice({
  name: 'sidebarTheme',
  initialState,
  reducers: {
    // Optimistic local switch, so the picker feels instant while the PUT runs.
    setSidebarThemeLocal(state, action: PayloadAction<SidebarThemeId>) {
      state.theme = action.payload;
      cacheSidebarTheme(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSidebarTheme.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSidebarTheme.fulfilled, (state, action) => {
        state.status = 'loaded';
        state.theme = action.payload;
        cacheSidebarTheme(action.payload);
      })
      .addCase(fetchSidebarTheme.rejected, (state, action) => {
        // Keep whatever is on screen; a failed read must not restyle the app.
        state.status = 'error';
        state.error = action.error.message ?? 'Failed to load sidebar theme';
      })
      .addCase(saveSidebarTheme.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveSidebarTheme.fulfilled, (state, action) => {
        state.saving = false;
        state.status = 'loaded';
        state.theme = action.payload;
        cacheSidebarTheme(action.payload);
      })
      .addCase(saveSidebarTheme.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? 'Failed to save sidebar theme';
      })
      // End of session: drop back to the default and forget the cached value.
      // Without this the next person to log in on this browser would briefly
      // wear the previous user's theme (the cache is read once, at app boot).
      .addMatcher(
        (action) => action.type === 'auth/logout/fulfilled' || action.type === 'auth/clearAuth',
        (state) => {
          state.theme = DEFAULT_SIDEBAR_THEME;
          state.status = 'idle';
          state.saving = false;
          state.error = null;
          clearCachedSidebarTheme();
        }
      );
  },
});

export const { setSidebarThemeLocal } = sidebarThemeSlice.actions;
export default sidebarThemeSlice.reducer;
